import PolicyLayout, { Section } from "@/components/site/PolicyLayout";
import usePageMeta from "@/hooks/usePageMeta";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy | RUDRA EMPIRE",
    description:
      "Privacy Policy — how RUDRA EMPIRE collects, uses and protects limited personal information submitted through this website.",
    canonical: "https://rudraempire.com/privacy",
  });
  return (
    <PolicyLayout
      eyebrow="Policy · I"
      title="Privacy"
      italic="Policy."
      lastUpdated="August 2026"
    >
      <p className="text-lg text-neutral-200 italic">
        Discretion is the founding virtue of this house. This policy explains
        what limited data we collect, why we collect it, and how we protect it.
      </p>

      <Section number="01" title="Who We Are">
        <p>
          RUDRA EMPIRE ("we", "our", "us") is a private land acquisition and
          consolidation company, founded in 1995 and operating exclusively in
          Karnataka, India. Our registered correspondence email is{" "}
          <a className="link-gold text-neutral-100" href="mailto:info@rudraempire.com">
            info@rudraempire.com
          </a>.
        </p>
      </Section>

      <Section number="02" title="Information We Collect">
        <p>We collect only what is necessary to respond to your enquiry or manage the internal operations of the firm:</p>
        <ul className="list-none space-y-3 mt-3">
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Name, email address, and (optionally) phone number and message content that you voluntarily submit via our public enquiry form.</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Employee account details (name, email, role, designation) added by an authorised administrator inside our internal Employee Portal.</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Basic technical logs (IP address, browser type, timestamps) collected by our hosting provider for security and diagnostics.</li>
        </ul>
        <p>We do <span className="text-neutral-100">not</span> place advertising cookies, share your data with data brokers, or profile visitors.</p>
      </Section>

      <Section number="03" title="How We Use Your Information">
        <ul className="list-none space-y-3">
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> To respond to enquiries you send us through the website.</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> To operate the internal Employee Portal (project pipeline, assignments, notes, documents).</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> To send transactional email notifications to the firm when a new enquiry is submitted.</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> To secure the website against abuse.</li>
        </ul>
      </Section>

      <Section number="04" title="Third-Party Processors">
        <p>We rely on a small number of trusted service providers to deliver the website itself. Where any of the following process personal data on our behalf, they are contractually bound to protect it:</p>
        <ul className="list-none space-y-3 mt-3">
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Hosting and infrastructure (for serving the site and database).</li>
          <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Transactional email delivery (to relay enquiry alerts to our office inbox).</li>
        </ul>
      </Section>

      <Section number="05" title="Retention">
        <p>
          Enquiries are retained for as long as they remain relevant to an active conversation or business relationship, and no longer than reasonably necessary. Employee portal records are retained for the duration of employment plus a limited period thereafter for legal compliance.
        </p>
      </Section>

      <Section number="06" title="Your Rights">
        <p>
          You may request access to, correction of, or deletion of any personal data we hold about you by writing to{" "}
          <a className="link-gold text-neutral-100" href="mailto:info@rudraempire.com">info@rudraempire.com</a>.
          We will respond within a reasonable period, subject to any retention obligations under applicable law.
        </p>
      </Section>

      <Section number="07" title="Security">
        <p>
          The site is served over HTTPS. Employee portal passwords are stored using industry-standard bcrypt hashing and never in plaintext. Access to the portal is invitation-only and role-restricted.
        </p>
      </Section>

      <Section number="08" title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. Any material change will be communicated by updating the "Last updated" date at the top of this page.
        </p>
      </Section>

      <Section number="09" title="Contact">
        <p>
          For any privacy-related question, please write to{" "}
          <a className="link-gold text-neutral-100" href="mailto:info@rudraempire.com">info@rudraempire.com</a>.
        </p>
      </Section>
    </PolicyLayout>
  );
}
